import { BusinessScope } from "./businessScope";

const TOGGLE = "TOGGLE";

export type BusinessAnswer = { operatorId: string; response: string[] };

type ScopedQuestion = {
  _id?: unknown;
  type?: string | null;
  parentQuestion?: unknown;
  businessScope?: string | null;
};

type ScopedResponse = {
  questionData?: ScopedQuestion | null;
  response?: (string | null | undefined)[] | null;
  byBusiness?: (BusinessAnswer | null | undefined)[] | null;
};

export const isPerBusinessToggle = (
  question: ScopedQuestion | null | undefined,
): boolean =>
  question?.type === TOGGLE && question.businessScope === BusinessScope.PerBusiness;

/** Whether any question in the list collects answers per business. */
export const hasPerBusinessQuestions = (
  questions: readonly (ScopedQuestion | null | undefined)[],
): boolean => questions.some(isPerBusinessToggle);

const idOf = (value: unknown) =>
  value == null ? "" : String((value as { _id?: unknown })._id ?? value);

function findToggle<T extends ScopedResponse>(
  question: ScopedQuestion | null | undefined,
  responses: readonly (T | null | undefined)[],
): T | undefined {
  if (!question?.parentQuestion) return undefined;
  const parentId = idOf(question.parentQuestion);
  return (responses.find(
    r => r?.questionData?._id != null && idOf(r.questionData._id) === parentId,
  ) ?? undefined) as T | undefined;
}

/** The per-business toggle a follow-up question is answered under, if any. */
export function getScopingToggle<T extends ScopedResponse>(
  question: ScopedQuestion | null | undefined,
  responses: readonly (T | null | undefined)[],
): T | undefined {
  if (!question || question.type === TOGGLE) return undefined;
  const parent = findToggle(question, responses);
  return parent && isPerBusinessToggle(parent.questionData) ? parent : undefined;
}

/** Businesses a per-business toggle is switched on for, in answer order. */
export const getEnabledBusinessIds = (
  toggle: ScopedResponse | null | undefined,
): string[] =>
  (toggle?.byBusiness ?? [])
    .filter((b): b is BusinessAnswer => !!b?.operatorId && b.response?.[0] === "true")
    .map(b => String(b.operatorId));

export const getBusinessAnswer = (
  response: ScopedResponse | null | undefined,
  operatorId: string,
): string[] =>
  (response?.byBusiness ?? []).find(b => b && String(b.operatorId) === operatorId)
    ?.response ?? [];

/** Replace one business's answer, keeping the others. */
export function setBusinessAnswer(
  byBusiness: readonly (BusinessAnswer | null | undefined)[] | null | undefined,
  operatorId: string,
  response: string[],
): BusinessAnswer[] {
  const rest = (byBusiness ?? []).filter(
    (b): b is BusinessAnswer => !!b && String(b.operatorId) !== operatorId,
  );
  return [...rest, { operatorId, response }];
}

/**
 * Makes per-business answers consistent with the submission's businesses
 * (`selectedOperatorIds`, primary first):
 * - a per-business toggle keeps one `["true"]` entry per selected business it's
 *   on for, and its own `response` is `"true"` while it's on for any of them,
 *   so shared visibility rules hold;
 * - its follow-ups keep answers only for those businesses, in that order, and
 *   leave their shared `response` empty;
 * - every other question drops `byBusiness`.
 * Without businesses there is nothing to answer per business, so per-business
 * toggles behave like ordinary toggles and all `byBusiness` answers are dropped.
 */
export function normalizeBusinessScopedResponses<T extends ScopedResponse>(
  responses: readonly (T | null | undefined)[],
  selectedOperatorIds?: readonly string[] | null,
): (T | null | undefined)[] {
  const selected = (selectedOperatorIds ?? []).map(String);
  const dropByBusiness = (r: T | null | undefined) => {
    if (!r || r.byBusiness === undefined) return r;
    const unscoped = { ...r };
    delete unscoped.byBusiness;
    return unscoped;
  };
  if (selected.length === 0) return responses.map(dropByBusiness);

  const withToggles = responses.map(r => {
    if (!r || !isPerBusinessToggle(r.questionData)) return r;
    const enabled = new Set(getEnabledBusinessIds(r));
    const on = selected.filter(id => enabled.has(id));
    return {
      ...r,
      response: [on.length > 0 ? "true" : "false"],
      byBusiness: on.map(operatorId => ({ operatorId, response: ["true"] })),
    };
  });

  return withToggles.map(r => {
    if (!r || isPerBusinessToggle(r.questionData)) return r;
    const toggle = getScopingToggle(r.questionData, withToggles);
    if (!toggle) return dropByBusiness(r);
    const on = getEnabledBusinessIds(toggle);
    return {
      ...r,
      response: [],
      byBusiness: on.map(operatorId => ({
        operatorId,
        response: getBusinessAnswer(r, operatorId),
      })),
    };
  });
}
